import * as React from 'react';

import { Checkbox } from '@coursera/cds-core';

import _t from 'i18n!nls/admin-shared';

import useStringSet from '../hooks/useStringSet';
import type { ReplaceSet } from '../hooks/useStringSet';

/** How much of the current page is selected */
type SelectionState = 'NONE' | 'SOME' | 'ALL';

interface TableMultiSelectContext {
  select: (id: string) => void;
  unSelect: (id: string) => void;
  isSelected: (id: string) => boolean;
  selectAll: () => void;
  unSelectAll: () => void;
  selectionState: SelectionState;
  selection: Set<string>;
}

const Context = React.createContext<TableMultiSelectContext>({
  select: () => undefined,
  unSelect: () => undefined,
  isSelected: () => false,
  selectAll: () => undefined,
  unSelectAll: () => undefined,
  selectionState: 'NONE',
  selection: new Set<string>(),
});

interface TableMultiSelectProps<TableRowType> {
  visibleRows: TableRowType[];
  keyFromRow: (row: TableRowType) => string;
}

type TableMultiSelectComponentProps<TableRowType> = TableMultiSelectProps<TableRowType> & {
  children: React.ReactNode;
};

const determineSelectionState = (setLength: number, rowsLength: number): SelectionState => {
  if (setLength === 0) {
    return 'NONE';
  }

  if (setLength === rowsLength) {
    return 'ALL';
  }

  return 'SOME';
};

const collectionsAreIdentical = <TableRowType,>(
  set1: TableRowType[],
  set2: TableRowType[],
  keyFromRow: (row: TableRowType) => string
) => {
  if (set1.length !== set2.length) {
    return false;
  }

  const set1Keys = set1.map(keyFromRow).sort();
  const set2Keys = set2.map(keyFromRow).sort();

  return set1Keys.every((set1Key, index) => set1Key === set2Keys[index]);
};

export interface UseClearSelectionIfRowsChangeArgs<T> {
  visibleRows: T[];
  replaceSelection: ReplaceSet;
  keyFromRow: (row: T) => string;
}
/**
 * If a user moves from page to page, or if the rows being displayed change by any other way, the user's current selection
 * may no longer make sense. This diffs the previous and current visible rows, and decides whether to clear the user's selection
 */
export const useClearSelectionIfRowsChange = <TableRowType,>(
  props: UseClearSelectionIfRowsChangeArgs<TableRowType>
) => {
  const { visibleRows, replaceSelection, keyFromRow } = props;
  const previousVisibleRows = React.useRef<TableRowType[]>(visibleRows);

  React.useEffect(() => {
    if (
      !previousVisibleRows.current ||
      !collectionsAreIdentical(visibleRows, previousVisibleRows.current, keyFromRow)
    ) {
      replaceSelection([]);
    }

    previousVisibleRows.current = visibleRows;
  }, [keyFromRow, replaceSelection, visibleRows]);
};

/**
 * Provides multi-select capabilities to a table.
 */
export const TableMultiSelect = <TableRowType,>({
  children,
  visibleRows,
  keyFromRow,
}: TableMultiSelectComponentProps<TableRowType>) => {
  const { addToSet: select, removeFromSet: unSelect, set: selection, replaceSet } = useStringSet();
  useClearSelectionIfRowsChange({ visibleRows, replaceSelection: replaceSet, keyFromRow });

  const isSelected = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (id: string) => selection.has(id),
    [selection]
  );
  const selectAll = React.useCallback<() => $TSFixMe>(() => {
    replaceSet(visibleRows.map(keyFromRow));
  }, [keyFromRow, replaceSet, visibleRows]);

  const unSelectAll = React.useCallback<() => $TSFixMe>(() => {
    replaceSet([]);
  }, [replaceSet]);

  const selectionState = determineSelectionState(selection.size, visibleRows.length);

  return (
    <Context.Provider
      value={{
        select,
        unSelect,
        isSelected,
        selectAll,
        unSelectAll,
        selectionState,
        selection,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const useTableMultiSelect = () => React.useContext(Context);

interface TableMultiSelectCheckboxProps {
  id: string;
}
export const TableMultiSelectCheckbox = ({ id }: TableMultiSelectCheckboxProps) => {
  const { select, unSelect, isSelected } = useTableMultiSelect();

  const selected = isSelected(id);

  return (
    <Checkbox
      aria-label={_t('Select row')}
      checked={selected}
      onChange={() => (selected ? unSelect(id) : select(id))}
      label=""
    />
  );
};

export const TableMultiSelectSelectAllCheckbox = () => {
  const { selectAll, unSelectAll, selectionState } = useTableMultiSelect();

  const handleNextClick = () => {
    // None -> All
    if (selectionState === 'NONE') {
      return selectAll();
    }

    // All -> None
    // Some -> None
    return unSelectAll();
  };

  return (
    <Checkbox
      aria-label={_t('Select all rows')}
      checked={selectionState === 'ALL'}
      indeterminate={selectionState === 'SOME'}
      onChange={handleNextClick}
      label=""
    />
  );
};
